import { useState } from 'react'
import { Loader, AlertCircle, CheckCircle2, Link2Off, RefreshCw } from 'lucide-react'
import GoogleConnectButton from '../registration/GoogleConnectButton'
import { saveGoogleCredentials } from '../../services/registrationApi'
import { disconnectJudgeMe } from '../../services/judgemeApi'
import JudgeMeConnectModal from './JudgeMeConnectModal'

interface LocationCardProps {
  locationId: string
  locationName: string
  notificationEmail: string
  planName: string
  hasGoogleCredentials: boolean
  onGetReviews: () => Promise<number>
  onCredentialsSaved: () => void
  // Judge.me (optional — omit to hide the section entirely)
  hasJudgeMeConnection?: boolean
  judgeMeShopDomain?: string | null
  onJudgeMeConnected?: () => void
  onJudgeMeDisconnected?: () => void
}

export default function LocationCard({
  locationId,
  locationName,
  notificationEmail,
  planName,
  hasGoogleCredentials,
  onGetReviews,
  onCredentialsSaved,
  hasJudgeMeConnection = false,
  judgeMeShopDomain = null,
  onJudgeMeConnected,
  onJudgeMeDisconnected,
}: LocationCardProps) {
  const jwt = localStorage.getItem('jwt_token') ?? ''

  const [isFetching, setIsFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [fetchCount, setFetchCount] = useState<number | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const [showReconnect, setShowReconnect] = useState(false)

  const [showJudgeMeModal, setShowJudgeMeModal] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [judgeMeError, setJudgeMeError] = useState<string | null>(null)

  const canConnectJudgeMe = planName === 'pro' || planName === 'agency'

  const handleGetReviews = async () => {
    setIsFetching(true)
    setFetchError(null)
    setFetchCount(null)
    try {
      const count = await onGetReviews()
      setFetchCount(count)
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setIsFetching(false)
    }
  }

  const handleConnect = async (encryptedCredentials: string) => {
    setSaveError(null)
    try {
      await saveGoogleCredentials(locationId, encryptedCredentials)
      onCredentialsSaved()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save credentials.')
    }
  }

  const handleJudgeMeDisconnect = async () => {
    setJudgeMeError(null)
    setIsDisconnecting(true)
    try {
      await disconnectJudgeMe(jwt, locationId)
      onJudgeMeDisconnected?.()
    } catch (err) {
      setJudgeMeError(err instanceof Error ? err.message : 'Failed to disconnect.')
    } finally {
      setIsDisconnecting(false)
    }
  }

  return (
    <div className="bg-rd-surface border border-white/[0.07] rounded-2xl p-6 space-y-4">
      {/* Location name */}
      <h2 className="font-sora font-semibold text-lg text-rd-text">{locationName}</h2>

      {/* Info rows */}
      <dl className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <dt className="text-rd-muted w-36 shrink-0">Notification email</dt>
          <dd className="text-rd-text truncate">{notificationEmail}</dd>
        </div>

        <div className="flex items-center gap-2">
          <dt className="text-rd-muted w-36 shrink-0">Plan</dt>
          <dd className="text-rd-text capitalize">{planName}</dd>
        </div>

        <div className="flex items-center gap-2">
          <dt className="text-rd-muted w-36 shrink-0">Google profile</dt>
          <dd className="flex items-center gap-1.5">
            {hasGoogleCredentials ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-rd-accent" />
                <span className="text-rd-accent text-sm">Connected</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4 text-warning-500" />
                <span className="text-warning-500 text-sm">Not connected</span>
              </>
            )}
          </dd>
        </div>
      </dl>

      {/* Google connect — shown only when not yet connected */}
      {!hasGoogleCredentials && (
        <div className="pt-1 space-y-2">
          <p className="text-xs text-rd-muted">
            Connect your Google Business Profile to enable automatic review fetching.
          </p>
          <GoogleConnectButton
            isConnected={false}
            onConnect={handleConnect}
          />
          {saveError && (
            <p className="text-sm text-danger-500 flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4" />
              {saveError}
            </p>
          )}
        </div>
      )}

      {/* Get Reviews — shown only when connected */}
      {hasGoogleCredentials && (
        <div className="pt-2">
          {/* Primary action */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleGetReviews}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #4ade80, #22d3ee)', color: '#0a0a0f' }}
            >
              {isFetching && <Loader className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {isFetching ? 'Fetching…' : 'Get My Reviews'}
            </button>

            {fetchCount !== null && fetchCount > 0 && (
              <p className="text-sm text-rd-accent flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                {fetchCount === 1 ? '1 new review fetched.' : `${fetchCount} new reviews fetched.`}
                {' '}Check the Reviews tab to see them.
              </p>
            )}

            {fetchCount === 0 && !fetchError && (
              <p className="text-sm text-rd-muted flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-rd-dim" />
                All caught up — no new reviews since last sync.
              </p>
            )}

            {fetchError && (
              <p className="text-sm text-danger-500 flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4" />
                {fetchError}
              </p>
            )}
          </div>

          {/* Reconnect — visually separated as a secondary action */}
          <div className="mt-4 pt-3 border-t border-white/[0.06]">
            {!showReconnect ? (
              <button
                type="button"
                onClick={() => setShowReconnect(true)}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-warning-500/10 border border-warning-500/30 text-warning-500 hover:bg-warning-500/15 hover:border-warning-500/50 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Token expired? Reconnect Google
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-rd-muted">
                  Re-authorize to replace the current Google credentials.
                </p>
                <GoogleConnectButton isConnected={false} onConnect={handleConnect} />
                {saveError && (
                  <p className="text-sm text-danger-500 flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4" />
                    {saveError}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => { setShowReconnect(false); setSaveError(null) }}
                  className="text-xs text-rd-dim hover:text-rd-muted transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Judge.me connection ─────────────────────────────────────────── */}
      <div className="border-t border-white/[0.06] pt-4 mt-2 space-y-3">
        <div className="flex items-center gap-2">
          <dt className="text-rd-muted text-sm w-36 shrink-0">Judge.me</dt>
          <dd className="flex items-center gap-1.5">
            {hasJudgeMeConnection && (
              <>
                <CheckCircle2 className="w-4 h-4 text-rd-accent" />
                <span className="text-rd-accent text-sm">Connected</span>
                {judgeMeShopDomain && (
                  <span className="text-rd-dim text-xs">({judgeMeShopDomain})</span>
                )}
              </>
            )}
          </dd>
        </div>

        {hasJudgeMeConnection ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleJudgeMeDisconnect}
              disabled={isDisconnecting}
              className="inline-flex items-center gap-1.5 text-xs text-rd-muted hover:text-danger-500 transition-colors disabled:opacity-40"
            >
              <Link2Off className="w-3.5 h-3.5" />
              {isDisconnecting ? 'Disconnecting…' : 'Disconnect Judge.me'}
            </button>
            {judgeMeError && (
              <p className="text-sm text-danger-500 flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4" />
                {judgeMeError}
              </p>
            )}
          </div>
        ) : canConnectJudgeMe ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => { setJudgeMeError(null); setShowJudgeMeModal(true) }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-white/10 text-rd-text hover:border-white/20 hover:bg-white/[0.03] transition-colors"
            >
              Connect Judge.me
            </button>
            {judgeMeError && (
              <p className="text-sm text-danger-500 flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4" />
                {judgeMeError}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-rd-muted">
            Available on <span className="text-rd-text font-medium">Pro</span> and{' '}
            <span className="text-rd-text font-medium">Agency</span> plans.
          </p>
        )}
      </div>

      {/* Judge.me connect modal */}
      {showJudgeMeModal && (
        <JudgeMeConnectModal
          jwt={jwt}
          locationId={locationId}
          onSuccess={() => {
            setShowJudgeMeModal(false)
            onJudgeMeConnected?.()
          }}
          onClose={() => setShowJudgeMeModal(false)}
        />
      )}
    </div>
  )
}
